import { useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '../../hooks/useMediaQuery.ts';

export function useReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

const ease = (t: number) => 1 - Math.pow(1 - t, 3);

/** Animates from the last shown value to `target` (ease-out, ~1 s); jumps straight there for reduced motion. */
export function useCountUp(target: number, ms = 1100): number {
  const reduced = useReducedMotion();
  const [shown, setShown] = useState(reduced || !Number.isFinite(target) ? target : 0);
  const from = useRef(shown);
  useEffect(() => {
    if (reduced || !Number.isFinite(target)) {
      setShown(target);
      from.current = target;
      return;
    }
    const start = performance.now();
    const a = Number.isFinite(from.current) ? from.current : 0;
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / ms);
      const v = p === 1 ? target : a + (target - a) * ease(p);
      setShown(v);
      from.current = v;
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms, reduced]);
  return shown;
}

/** A number that counts up to its value; renders `—` while the value is unknown. */
export function CountUp({ value, format, className = 'num' }: { value: number | undefined; format(v: number): string; className?: string }) {
  const v = useCountUp(value ?? NaN);
  return <span className={className}>{value === undefined || !Number.isFinite(value) ? '—' : format(v)}</span>;
}
